import { RestApiClient, MemoryTokenManager } from '@docyrus/api-client';

import { DEFAULT_API_URL, DEFAULT_SSR_COOKIE_KEY } from '../constants';
import {
  type MiddlewareRequest,
  type ServerClientConfig,
  type AuthMiddlewareConfig,
  type AuthSession
} from './types';

/**
 * Read the auth session from middleware request cookies.
 *
 * Use this when you need full control over your middleware logic.
 *
 * @example
 * ```ts
 * import { NextResponse, type NextRequest } from 'next/server';
 * import { getMiddlewareSession } from '@docyrus/signin/nextjs';
 *
 * export function middleware(request: NextRequest) {
 *   const session = getMiddlewareSession(request);
 *   if (!session.isAuthenticated && request.nextUrl.pathname.startsWith('/dashboard')) {
 *     return NextResponse.redirect(new URL('/login', request.url));
 *   }
 *   return NextResponse.next();
 * }
 * ```
 */
export function getMiddlewareSession(
  request: MiddlewareRequest,
  cookieKey?: string
): AuthSession {
  const key = cookieKey ?? DEFAULT_SSR_COOKIE_KEY;
  const tokenCookie = request.cookies.get(key);

  const accessToken = tokenCookie?.value ? decodeURIComponent(tokenCookie.value) : null;

  return {
    accessToken,
    isAuthenticated: accessToken !== null
  };
}

/**
 * Create an authenticated `RestApiClient` for use in Next.js middleware.
 *
 * @example
 * ```ts
 * import { type NextRequest } from 'next/server';
 * import { createMiddlewareClient } from '@docyrus/signin/nextjs';
 *
 * export function middleware(request: NextRequest) {
 *   const client = createMiddlewareClient(request);
 *   // use client for API calls if needed
 * }
 * ```
 */
export function createMiddlewareClient(
  request: MiddlewareRequest,
  config: ServerClientConfig = {}
): RestApiClient {
  const apiUrl = config.apiUrl ?? DEFAULT_API_URL;
  const cookieKey = config.cookieKey ?? DEFAULT_SSR_COOKIE_KEY;

  const token = request.cookies.get(cookieKey)?.value ?? null;
  const decodedToken = token ? decodeURIComponent(token) : null;

  const tokenManager = new MemoryTokenManager();

  if (decodedToken) {
    tokenManager.setToken(decodedToken);
  }

  return new RestApiClient({
    baseURL: apiUrl,
    tokenManager
  });
}

/**
 * Check if a pathname matches any of the given route patterns.
 * Supports `(.*)` suffix for catch-all matching.
 */
function matchesRoute(pathname: string, patterns: string[]): boolean {
  return patterns.some((pattern) => {
    const regexStr = pattern
      .replace(/[.+?^${}|[\]\\]/g, '\\$&')
      .replace(/\\\(\\\.\\\*\\\)/g, '.*')
      .replace(/\\\*/g, '[^/]*');

    return new RegExp(`^${regexStr}$`).test(pathname);
  });
}

const DEFAULT_PUBLIC_ROUTES = ['/login', '/auth/callback'];

/**
 * Pre-built auth middleware for route protection.
 *
 * Uses standard `Response.redirect()` for redirects and returns `undefined`
 * for pass-through (Next.js treats this as "continue to next middleware/page").
 *
 * @example
 * ```ts
 * // middleware.ts
 * import { authMiddleware } from '@docyrus/signin/nextjs';
 *
 * export default authMiddleware({
 *   publicRoutes: ['/login', '/callback'],
 *   loginPath: '/login',
 * });
 *
 * export const config = {
 *   matcher: ['/((?!_next/static|_next/image|favicon.ico|.*\\..*).*)'],
 * };
 * ```
 */
export function authMiddleware(config: AuthMiddlewareConfig = {}) {
  const {
    protectedRoutes,
    publicRoutes = DEFAULT_PUBLIC_ROUTES,
    loginPath = '/login',
    afterLoginPath = '/',
    cookieKey = DEFAULT_SSR_COOKIE_KEY
  } = config;

  const allPublicRoutes = publicRoutes.includes(loginPath) ? publicRoutes : [...publicRoutes, loginPath];

  return (request: MiddlewareRequest): Response | undefined => {
    const { pathname } = request.nextUrl;
    const session = getMiddlewareSession(request, cookieKey);

    const isPublicRoute = matchesRoute(pathname, allPublicRoutes);
    const isProtectedRoute = protectedRoutes ? matchesRoute(pathname, protectedRoutes) : !isPublicRoute;

    // Redirect authenticated users away from login page
    if (session.isAuthenticated && pathname === loginPath) {
      return Response.redirect(new URL(afterLoginPath, request.url));
    }

    // Redirect unauthenticated users to login
    if (!session.isAuthenticated && isProtectedRoute) {
      return Response.redirect(new URL(loginPath, request.url));
    }

    // Pass through
    return undefined;
  };
}
